import { useMemo } from "react"

import {
  DELETE_ATTACHMENT_OVERLAY,
  REPLACE_ATTACHMENT_OVERLAY
} from "shared/context/overlay/types"
import { groupBy } from "shared/lib/arrayUtils"
import { useEvent } from "shared/lib/useEvent"
import { useOverlay } from "shared/lib/useOverlay"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { ProjectsAPIType } from "shared/types/Project"
import { AttachmentType } from "shared/types/__generated__/graphql"

import { useProjectAttachmentUpload } from "entities/attachments/api/useProjectAttachmentUpload"
import { useAttachmentsLazyQuery } from "entities/attachments/graphql/project-attachments.queries.generated"
import { useProjectAttachmentsData } from "entities/projects/api/useProjectAttachmentsData"
import { useProjectPermissions } from "entities/projects/model/useProjectPermissions"
import { ProjectPermissionType } from "entities/projects/types/ProjectPermissions"

import { ProjectAttachmentsModel } from "../ProjectAttachments"
import { useProjectAttachmentAcceptedFileTypes } from "./useProjectAttachmentAcceptedFileTypes"

interface UseProjectAttachmentsModelArgs {
  projectID: string
}

export function useProjectAttachmentsModel({
  projectID
}: UseProjectAttachmentsModelArgs): ProjectAttachmentsModel {
  const { attachments, attachmentCategories } = useProjectAttachmentsData({
    projectID
  })

  const {
    acceptsByCategory,
    isLoading: acceptedFileTypesLoading,
    error: acceptedFileTypesError
  } = useProjectAttachmentAcceptedFileTypes()

  const [fetchAttachments] = useAttachmentsLazyQuery({
    variables: { id: projectID, type: ProjectsAPIType },
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })

  const { createAttachments } = useProjectAttachmentUpload()
  const { openOverlay: openDeleteAttachmentOverlay } = useOverlay(
    DELETE_ATTACHMENT_OVERLAY
  )
  const { openOverlay: openReplaceAttachmentOverlay } = useOverlay(
    REPLACE_ATTACHMENT_OVERLAY
  )

  const { permissions } = useProjectPermissions({ projectID })

  const handleUpload = useEvent(
    async (files: File | File[], category: string) => {
      const fileList = Array.isArray(files) ? files : [files]
      return createAttachments(fileList, { projectID, category })
    }
  )

  const openAttachmentDeleteConfirmation = useEvent((file: AttachmentType) => {
    openDeleteAttachmentOverlay({ projectID, attachment: file })
  })

  const openAttachmentReplaceOverlay = useEvent((file: AttachmentType) => {
    openReplaceAttachmentOverlay({ projectID, attachment: file })
  })

  const filesByCategory = useMemo(() => {
    return groupBy(attachments?.data || [], file => file.category || "unknown")
  }, [attachments?.data])

  return {
    files: attachments?.data,
    categories: attachmentCategories?.data,
    acceptsByCategory,
    filesByCategory,
    isLoading:
      attachments?.isLoading ||
      attachmentCategories?.isLoading ||
      acceptedFileTypesLoading,
    isError:
      attachments?.error ||
      attachmentCategories?.error ||
      acceptedFileTypesError,
    isReadonly: !permissions[ProjectPermissionType.EDIT],
    onUpload: handleUpload,
    onUploadComplete: fetchAttachments,
    onDelete: openAttachmentDeleteConfirmation,
    onReplace: openAttachmentReplaceOverlay
  }
}
